import React from "react";
import { Box, Card, Grid, Skeleton, Stack, Typography } from "@mui/material";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ClassOutlinedIcon from "@mui/icons-material/ClassOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import EventSeatOutlinedIcon from "@mui/icons-material/EventSeatOutlined";
import ChairAltOutlinedIcon from "@mui/icons-material/ChairAltOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";

type Summary = {
  total_staff?: number;
  total_active_classes?: number;
  total_active_students?: number;
  total_capacity?: number;
  available_seats?: number;
  full_classes?: number;
  classes_no_students?: number;
  capacity_data_issues?: number;
};

// Compact coloured summary cards: 4 across desktop, 2 tablet, 1 mobile.
// Each card shows a big value with an icon and a short supporting caption.
const CARDS = [
  { key: "total_staff",           label: "Total Staff",      sub: "Distinct staff in the current view",
    color: "#1d4ed8", bg: "#dbeafe", Icon: BadgeOutlinedIcon },
  { key: "total_active_classes",  label: "Active Classes",   sub: "Classes visible under filters",
    color: "#6b21a8", bg: "#f3e8ff", Icon: ClassOutlinedIcon },
  { key: "total_active_students", label: "Active Students",  sub: "Distinct enrolments across classes",
    color: "#166534", bg: "#dcfce7", Icon: PeopleAltOutlinedIcon },
  { key: "total_capacity",        label: "Total Capacity",   sub: "Sum of configured class capacities",
    color: "#3730a3", bg: "#e0e7ff", Icon: EventSeatOutlinedIcon },
  { key: "available_seats",       label: "Available Seats",  sub: "Seats remaining across active classes",
    color: "#0f766e", bg: "#ccfbf1", Icon: ChairAltOutlinedIcon },
  { key: "full_classes",          label: "Full Classes",     sub: "Enrolment reached capacity",
    color: "#92400e", bg: "#fef3c7", Icon: EventBusyOutlinedIcon },
  { key: "classes_no_students",   label: "Empty Classes",    sub: "Zero students currently assigned",
    color: "#475569", bg: "#e2e8f0", Icon: HourglassEmptyOutlinedIcon },
  { key: "capacity_data_issues",  label: "Data Issues",      sub: "Enrolment exceeds configured capacity",
    color: "#b91c1c", bg: "#fee2e2", Icon: ReportProblemOutlinedIcon },
] as const;

export default function SummaryCards({ summary, loading }: { summary: Summary; loading: boolean }) {
  return (
    <Grid container spacing={1.5} mb={2}>
      {CARDS.map(({ key, label, sub, color, bg, Icon }) => (
        <Grid key={key} item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: "100%" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{
                width: 44, height: 44, borderRadius: "12px",
                background: bg, color, display: "flex",
                alignItems: "center", justifyContent: "center",
              }}>
                <Icon />
              </Box>
              <Box flex={1} minWidth={0}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                {loading ? <Skeleton width="60%" height={28} /> : (
                  <Typography variant="h5" fontWeight={700} color={color} lineHeight={1.15}>
                    {(summary[key as keyof Summary] ?? 0).toLocaleString()}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {sub}
                </Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}
